<template>
  <div class="signature-field-container" @click="handleClick">
    <img
      v-if="hasSignature"
      class="signature-img"
      :src="biometricSignature"
      :alt="t('signature')"
    />
    <div v-else :class="{ required: field.isRequired }">
      {{ placeholder }}
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Field } from '../types/field';

export default defineComponent({
  props: {
    field: {
      type: Object as PropType<Field>,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    fullName: {
      type: String,
      default: '',
    },
  },
  emits: ['click'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const placeholder = computed(() => props.field.label || defaultPlaceholder.value);

    const defaultPlaceholder = computed(() =>
      props.disabled ? t('waiting-for-signature') : t('write-your-signature-here')
    );

    const hasSignature = computed(() => !!props.field && !!props.field.signatureBiometric);

    const biometricSignature = computed(() => props.field.signatureBiometric);

    const handleClick = () => {
      if (!hasSignature.value && !props.disabled) {
        emit('click');
      }
    };

    return {
      placeholder,
      defaultPlaceholder,
      hasSignature,
      biometricSignature,
      handleClick,
      t,
    };
  },
});
</script>
<style lang="scss" scoped>
.signature-field-container {
  font-size: 50%;
  height: 100%;
  line-height: 1.5;
  overflow-wrap: break-word;
  word-wrap: break-word;
  color: var(--text-gray);
  cursor: pointer;

  .signature-img {
    object-fit: contain;
    height: 100%;
    width: 100%;
  }

  .printed-name {
    position: absolute;
    bottom: 0;
    right: 0;
    font-size: 1rem;
    color: var(--text-gray);
  }

  .required {
    &::after {
      content: '*';
      color: var(--error-dark);
    }
  }
}
</style>
